// src/components/account/AccountGateClient.tsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import QRCode from "qrcode";

import Section from "@/components/ui/Section";
import Wrapper from "@/components/ui/Wrapper";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { CountrySelect } from "@/components/ui/CountrySelect";
import SafeHtml from "@/components/ui/SafeHtml";
import { LOCALE_MAP } from "@/lib/localeMap";
import { DEFAULT_LANGUAGE } from "@/lib/languages";

import type { Me } from "@/lib/auth/getWpMe";
import type { Ticket } from "@/lib/auth/getTickets";
import api from "@/lib/auth/apiClient";
import { useLayoutData } from "../layout/LayoutDataProvider";

type Props = {
  lang: string;
  me: Me | null;
  tickets?: Ticket[];
};

type TFn = (k: string, fallback?: string) => string;

// Reads global_text.auth.account.* — shared by parent, TicketCard and DeleteAccountModal
function useAccountT(): TFn {
  const { global_text } = useLayoutData();
  const account =
    (global_text as { auth?: { account?: Record<string, string> } } | undefined)
      ?.auth?.account ?? {};
  return React.useCallback(
    (k: string, fallback = "") => account[k] || fallback,
    [account]
  );
}

function formatEventDate(dateStr: string, lang: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00");
  if (isNaN(d.getTime())) return dateStr;

  return d.toLocaleDateString(LOCALE_MAP[lang] ?? lang, {
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function safeStr(v: unknown) {
  return typeof v === "string" ? v : "";
}

function formatTimeString24(v: unknown): string {
  const raw = safeStr(v).trim();
  if (!raw) return "";

  const match = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) return raw.replace(/\s*(AM|PM)\s*$/i, "").trim();

  let h = Number(match[1]);
  const m = Number(match[2] ?? "0");
  const meridiem = match[3]?.toUpperCase();

  if (!Number.isFinite(h) || !Number.isFinite(m)) return raw;

  if (meridiem === "PM" && h < 12) h += 12;
  if (meridiem === "AM" && h === 12) h = 0;

  if (h < 0 || h > 23 || m < 0 || m > 59) return raw;

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function TicketQR({ ticketId }: { ticketId: string }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!ticketId) {
      setDataUrl(null);
      return;
    }

    QRCode.toDataURL(ticketId, {
      width: 280,
      margin: 2,
      color: { dark: "#111111", light: "#ffffff" },
    })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setDataUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  if (!ticketId) return null;

  return (
    <div className="flex flex-col items-center gap-3">
      {dataUrl ? (
        <Image
          src={dataUrl}
          alt={`QR ${ticketId}`}
          width={192}
          height={192}
          unoptimized
          className="h-48 w-48"
          style={{ imageRendering: "pixelated" }}
        />
      ) : (
        <div className="h-48 w-48 animate-pulse bg-black/5" />
      )}

      <span className="select-all font-mono text-xs tracking-wide opacity-40">
        #{ticketId}
      </span>
    </div>
  );
}

function TicketCard({ ticket, lang }: { ticket: Ticket; lang: string }) {
  const t = useAccountT();
  const [open, setOpen] = useState(false);
  const isPast = ticket.is_past;

  const isCancelled =
    ticket.status === "Cancelled" || ticket.status === "Canceled";

  const statusLabel =
    ticket.status === "Checked In"
      ? t("ticket_status_checked_in", "Checked in")
      : isCancelled
        ? t("ticket_status_cancelled", "Cancelled")
        : ticket.status === "Not Checked In"
          ? t("ticket_status_not_checked_in", "Not checked in")
          : null;

  const completedLabel = t("ticket_status_completed", "Completed");

  const statusDotColor =
    ticket.status === "Checked In"
      ? "#34d399"
      : isCancelled
        ? "#f87171"
        : "#fb923c";

  return (
    <div
      className="relative overflow-hidden"
      style={{
        background: isPast ? "#f4f3f0" : "#ffffff",
        border: isPast
          ? "1px solid rgba(0,0,0,0.07)"
          : "1px solid rgba(0,0,0,0.10)",
        borderTop: isPast
          ? "1px solid rgba(0,0,0,0.07)"
          : "3px solid var(--color-fujifilm-green, #7cd5a8)",
        boxShadow: open
          ? "0 8px 32px rgba(0,0,0,0.10)"
          : isPast
            ? "none"
            : "0 2px 16px rgba(0,0,0,0.06)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-stretch text-left"
      >
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 px-3 py-3">
          <div
            className="truncate font-semibold leading-snug"
            style={{ fontSize: "13px", opacity: isPast ? 0.5 : 1 }}
          >
            <SafeHtml content={ticket.event_name} />
          </div>

          {ticket.event_date && (
            <div
              className="truncate"
              style={{ fontSize: "11px", opacity: 0.45 }}
            >
              {formatEventDate(ticket.event_date, lang)}
              {ticket.event_time && ticket.event_time.trim() !== ":"
                ? ` · ${formatTimeString24(ticket.event_time)}`
                : ""}
            </div>
          )}

          {(isPast || statusLabel) && (
            <div className="mt-1 flex items-center gap-1.5">
              <span
                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                style={{
                  background: isPast ? "rgba(0,0,0,0.2)" : statusDotColor,
                }}
              />

              <span
                style={{ fontSize: "11px", opacity: isPast ? 0.35 : 0.65 }}
              >
                {isPast ? completedLabel : statusLabel}
              </span>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center pr-3">
          <svg
            className="h-4 w-4 opacity-25 transition-transform duration-200"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {open && (
        <div
          className="flex flex-col items-center gap-4 px-4 pb-6 pt-4"
          style={{ borderTop: "1px dashed rgba(0,0,0,0.10)" }}
        >
          <TicketQR ticketId={ticket.ticket_id} />

          <div className="flex w-full flex-col" style={{ fontSize: "12px" }}>
            {ticket.event_date && (
              <div className="flex items-center gap-2 py-2.5 opacity-60">
                <svg
                  className="h-3.5 w-3.5 shrink-0 opacity-50"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5"
                  />
                </svg>

                <span>
                  {formatEventDate(ticket.event_date, lang)}
                  {ticket.event_time && ticket.event_time.trim() !== ":"
                    ? ` · ${formatTimeString24(ticket.event_time)}`
                    : ""}
                </span>
              </div>
            )}

            {ticket.event_location && (
              <div className="flex items-start gap-2 py-2.5 opacity-60">
                <svg
                  className="mt-px h-3.5 w-3.5 shrink-0 opacity-50"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                  />
                </svg>

                <span>{ticket.event_location}</span>
              </div>
            )}

            {(isPast || statusLabel || ticket.ticket_type) && (
              <div className="flex items-center gap-2 py-2.5">
                {(isPast || statusLabel) && (
                  <>
                    <span
                      className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{
                        background: isPast
                          ? "rgba(0,0,0,0.2)"
                          : statusDotColor,
                      }}
                    />

                    <span style={{ opacity: 0.65 }}>
                      {isPast ? completedLabel : statusLabel}
                    </span>
                  </>
                )}

                {ticket.ticket_type && (
                  <span style={{ opacity: 0.35 }}>
                    {isPast || statusLabel ? "· " : ""}
                    {ticket.ticket_type}
                  </span>
                )}
              </div>
            )}

            {ticket.attendee_name?.trim() && (
              <div className="py-2.5 opacity-40">{ticket.attendee_name}</div>
            )}

            {ticket.event_url && (
              <div className="pt-3">
                <a
                  href={ticket.event_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline opacity-40 hover:opacity-70"
                >
                  {t("ticket_view_event_link", "View event →")}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EditableField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Input
      label={<span className="text-body-3 opacity-50">{label}</span>}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function DeleteAccountModal({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useAccountT();
  const [typed, setTyped] = useState("");

  const confirmWord = t("delete_confirm_word", "DELETE");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.4)" }}
    >
      <div
        className="flex w-full max-w-sm flex-col gap-4 bg-white p-6"
        style={{ border: "1px solid rgba(0,0,0,0.1)" }}
      >
        <div className="text-body-1 font-semibold">
          {t("delete_modal_title", "Delete account?")}
        </div>

        <p className="text-body-2 opacity-60">
          <SafeHtml
            content={t("delete_modal_message_html", "This action is permanent and cannot be undone. Type <strong>{word}</strong> to confirm.").replace("{word}", confirmWord)}
          />
        </p>

        <Input
          placeholder={t("delete_modal_placeholder", "Type {word}").replace("{word}", confirmWord)}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          invalid={typed.length > 0 && typed !== confirmWord}
        />

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 border border-black/20 py-2 text-body-2 transition-colors hover:bg-black/5"
          >
            {t("delete_modal_cancel", "Cancel")}
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={typed !== confirmWord}
            className="flex-1 py-2 text-body-2 font-medium text-white transition-colors"
            style={{
              background:
                typed === confirmWord ? "#ef4444" : "rgba(0,0,0,0.15)",
            }}
          >
            {t("delete_modal_confirm", "Delete")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AccountGateClient({ lang, me, tickets }: Props) {
  const t = useAccountT();
  const router = useRouter();
  const { templateRoutes } = useLayoutData();
  const routes = templateRoutes ?? {};
  const loginHref = routes["page-login.php"];
  const homeHref =
    lang === DEFAULT_LANGUAGE.polylangCode ? "/" : `/${lang}`;

  const attemptedRefresh = useRef(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [displayName, setDisplayName] = useState(me?.display_name ?? "");
  const [billing, setBilling] = useState({
    first_name: me?.billing?.first_name ?? "",
    last_name: me?.billing?.last_name ?? "",
    company: me?.billing?.company ?? "",
    address_1: me?.billing?.address_1 ?? "",
    address_2: me?.billing?.address_2 ?? "",
    city: me?.billing?.city ?? "",
    postcode: me?.billing?.postcode ?? "",
    country: me?.billing?.country ?? "",
    phone: me?.billing?.phone ?? "",
    nif: me?.billing?.nif ?? "",
  });

  useEffect(() => {
    if (me) return;
    if (!loginHref) return;
    if (attemptedRefresh.current) return;

    attemptedRefresh.current = true;

    (async () => {
      try {
        await api.post("/api/auth/token/refresh");
        router.refresh();
        return;
      } catch { }

      try {
        await api.post("/api/auth/logout");
      } catch { }

      router.replace(loginHref);
    })();
  }, [me, loginHref, router]);

  if (!me) return null;

  async function onLogout() {
    if (loggingOut) return;

    setLoggingOut(true);

    try {
      await api.post("/api/auth/logout");
    } catch { }

    router.replace(loginHref || homeHref);
  }

  async function saveAll() {
    setSaving(true);
    setSaved(false);

    try {
      await api.post("/api/auth/me/update", {
        display_name: displayName,
        billing,
      });
      router.refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Save failed:", err);
    }

    setSaving(false);
  }

  async function deleteAccount() {
    try {
      await api.post("/api/auth/me/delete");
      router.replace(homeHref);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  }

  const upcomingTickets = tickets?.filter((t) => !t.is_past) ?? [];
  const pastTickets = tickets?.filter((t) => t.is_past) ?? [];

  const ticketCount = tickets?.length ?? 0;
  const ticketCountLabel =
    ticketCount === 1
      ? t("ticket_count_singular", "ticket")
      : t("ticket_count_plural", "tickets");

  return (
    <>
      {showDeleteModal && (
        <DeleteAccountModal
          onConfirm={() => {
            setShowDeleteModal(false);
            void deleteAccount();
          }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      <Section>
        <Wrapper>
          <div className="mb-8 flex flex-col gap-1">
            <h1 className="text-title-1 font-semibold">
              {t("welcome_greeting", "Welcome")}, {me.display_name.split(" ")[0]}
            </h1>

            <p className="m-0 text-body-2 opacity-50">{me.email}</p>
          </div>

          <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-5">
            <div className="flex flex-col gap-4 lg:col-span-2">
              <div className="border border-black/10 bg-white">
                <div className="border-b border-black/10 px-4 py-3">
                  <div className="text-body-3 font-semibold uppercase tracking-wide opacity-40">
                    {t("profile_heading", "Profile")}
                  </div>
                </div>

                <div className="flex flex-col gap-4 overflow-visible px-4 py-4">
                  <EditableField
                    label={t("profile_name_label", "Name")}
                    value={displayName}
                    onChange={setDisplayName}
                  />

                  <div>
                    <div className="mb-0.5 text-body-3 opacity-50">
                      {t("profile_email_label", "Email")}
                    </div>
                    <div className="break-words text-body-2 font-medium">
                      {me.email}
                    </div>
                  </div>
                </div>
              </div>

              {me.billing && (
                <div className="border border-black/10 bg-white">
                  <div className="border-b border-black/10 px-4 py-3">
                    <div className="text-body-3 font-semibold uppercase tracking-wide opacity-40">
                      {t("billing_heading", "Billing Details")}
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 overflow-visible px-4 py-4 pb-5">
                    <div className="grid grid-cols-2 gap-4 overflow-visible pb-1">
                      <EditableField
                        label={t("billing_first_name", "First name")}
                        value={billing.first_name}
                        onChange={(v) =>
                          setBilling((b) => ({ ...b, first_name: v }))
                        }
                      />

                      <EditableField
                        label={t("billing_last_name", "Last name")}
                        value={billing.last_name}
                        onChange={(v) =>
                          setBilling((b) => ({ ...b, last_name: v }))
                        }
                      />
                    </div>

                    <EditableField
                      label={t("billing_company", "Company")}
                      value={billing.company}
                      onChange={(v) =>
                        setBilling((b) => ({ ...b, company: v }))
                      }
                    />

                    <EditableField
                      label={t("billing_address_1", "Address")}
                      value={billing.address_1}
                      onChange={(v) =>
                        setBilling((b) => ({ ...b, address_1: v }))
                      }
                    />

                    <EditableField
                      label={t("billing_address_2", "Address line 2")}
                      value={billing.address_2}
                      onChange={(v) =>
                        setBilling((b) => ({ ...b, address_2: v }))
                      }
                    />

                    <div className="grid grid-cols-2 gap-4 overflow-visible pb-1">
                      <EditableField
                        label={t("billing_city", "City")}
                        value={billing.city}
                        onChange={(v) =>
                          setBilling((b) => ({ ...b, city: v }))
                        }
                      />

                      <EditableField
                        label={t("billing_postcode", "Postcode")}
                        value={billing.postcode}
                        onChange={(v) =>
                          setBilling((b) => ({ ...b, postcode: v }))
                        }
                      />
                    </div>

                    <CountrySelect
                      label={<span className="text-body-3 opacity-50">{t("billing_country", "Country")}</span>}
                      value={billing.country}
                      onChange={(v) =>
                        setBilling((b) => ({ ...b, country: v }))
                      }
                      lang={lang}
                    />

                    <EditableField
                      label={t("billing_nif", "Tax ID")}
                      value={billing.nif}
                      onChange={(v) => setBilling((b) => ({ ...b, nif: v }))}
                    />

                    <EditableField
                      label={t("billing_phone", "Phone")}
                      value={billing.phone}
                      onChange={(v) =>
                        setBilling((b) => ({ ...b, phone: v }))
                      }
                    />

                    <Button
                      type="button"
                      center
                      onClick={saveAll}
                      disabled={saving}
                      loading={saving}
                      className="w-full hover:opacity-80"
                      bgStyle="bg-fuji-green hover:bg-fuji-green/80 border-fuji-green hover:border-fuji-green/80"
                      icon={<></>}
                    >
                      {saved
                        ? t("save_saved_button", "Saved ✓")
                        : t("save_button", "Save changes")}
                    </Button>
                  </div>
                </div>
              )}

              <div className="border border-black/10 bg-white">
                <div className="flex flex-col gap-2 px-4 py-4">
                  <Button
                    type="button"
                    center
                    onClick={onLogout}
                    disabled={loggingOut}
                    loading={loggingOut}
                  >
                    {t("logout_button", "Sign out")}
                  </Button>

                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(true)}
                    className="w-full py-2 text-center text-body-3 opacity-30 transition-all hover:text-red-500 hover:opacity-70"
                  >
                    {t("delete_account_button", "Delete account")}
                  </button>
                </div>
              </div>

              <div className="text-center text-body-3">
                <Link
                  className="underline opacity-40 hover:opacity-70"
                  href={homeHref}
                >
                  {t("back_to_site_link", "← Back to site")}
                </Link>
              </div>
            </div>

            <div className="flex flex-col gap-6 lg:col-span-3">
              <div className="flex items-baseline justify-between">
                <h2 className="text-title-3 font-semibold">
                  {t("tickets_heading", "My tickets")}
                </h2>

                {tickets !== undefined && tickets.length > 0 && (
                  <span className="text-body-3 opacity-40">
                    {tickets.length} {ticketCountLabel}
                  </span>
                )}
              </div>

              {tickets === undefined || tickets.length === 0 ? (
                <div className="border border-black/10 bg-black/5 px-4 py-10 text-center">
                  <p className="m-0 text-body-2 opacity-40">
                    {t("tickets_empty_message", "No tickets yet")}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-6">
                  {upcomingTickets.length > 0 && (
                    <div>
                      <div className="mb-2 text-body-3 font-semibold uppercase tracking-wide opacity-40">
                        {t("tickets_upcoming_heading", "Upcoming")}
                      </div>

                      <div className="flex flex-col gap-2">
                        {upcomingTickets.map((ticket) => (
                          <TicketCard key={ticket.key} ticket={ticket} lang={lang} />
                        ))}
                      </div>
                    </div>
                  )}

                  {pastTickets.length > 0 && (
                    <div>
                      <div className="mb-2 text-body-3 font-semibold uppercase tracking-wide opacity-40">
                        {t("tickets_past_heading", "Past")}
                      </div>

                      <div className="flex flex-col gap-2">
                        {pastTickets.map((ticket) => (
                          <TicketCard key={ticket.key} ticket={ticket} lang={lang} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Wrapper>
      </Section>
    </>
  );
}